/**
 * Users API — typed migration of the old UsersAPI.js
 * Endpoints kept identical so it works against existing backend.
 */
import apiClient, { setAuthToken, clearAuthToken } from './client';
import type { User, LoginRequest, LoginResponse, SignupRequest } from './types';

const UsersAPI = {
  /** Get currently authenticated user profile */
  async getMe(): Promise<User | undefined> {
    try {
      const { data } = await apiClient.get<User>('/users/me');
      // Backend returns {result: user} — unpack it
      const me = (data as any)?.result ?? data;
      if (me?.email || me?.id || (me as any)?._id) {
        localStorage.setItem('patient_me', JSON.stringify(me));
      }
      return me;
    } catch (e: any) {
      // On timeout or network error — return cached user if available
      if (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout')) {
        const cached = localStorage.getItem('patient_me');
        if (cached) {
          try { return JSON.parse(cached); } catch { /* ignore */ }
        }
      }
      // Any other error (e.g. 401) — return undefined, caller decides
      return undefined;
    }
  },

  /** Login with email + password, returns user profile on success */
  async login({ email, password }: LoginRequest): Promise<User> {
    const { data } = await apiClient.post<LoginResponse>('/login', {
      email,
      password,
    });
    if (data.error) {
      throw new Error(data.error.message || 'Invalid login or password');
    }
    setAuthToken(data.token);
    const me = await this.getMe();
    if (!me) {
      // Token saved but profile unavailable (server slow/down).
      // Return a minimal stub so the user gets into the app.
      const cached = localStorage.getItem('patient_me');
      if (cached) {
        try { return JSON.parse(cached); } catch { /* ignore */ }
      }
      throw new Error('Сервер не отвечает. Попробуйте ещё раз через несколько секунд.');
    }
    return me;
  },
  /** Register a new account via /users/signup */
  async signup(payload: SignupRequest): Promise<User> {
    const { data } = await apiClient.post<LoginResponse>(
      '/users/signup',
      payload,
    );
    if (data.error) {
      throw new Error(data.error.message || 'Signup failed');
    }
    // After creating the account, log in to get the auth token
    return this.login({ email: payload.email, password: payload.password });
  },

  /** Clear local auth state */
  logout(): void {
    clearAuthToken();
    localStorage.removeItem('patient_me');
  },

  /** Get all teachers (admin) */
  async getAllTeachers(): Promise<User[]> {
    const { data } = await apiClient.get<User[]>('/teachers/all');
    return data ?? [];
  },

  /** Get a single user by ID */
  async getUser(userId: string): Promise<User | undefined> {
    if (!userId) return undefined;
    const { data } = await apiClient.get<User[]>('/users/by_ids', {
      params: { usersIds: [userId] },
    });
    return data[0];
  },

  /** Get multiple users by IDs */
  async getUsersByIds(ids: string[]): Promise<User[]> {
    const filtered = ids.filter(Boolean);
    if (filtered.length === 0) return [];
    const { data } = await apiClient.get<User[]>('/users/by_ids', {
      params: { usersIds: filtered },
    });
    return data;
  },

  /** Update user profile */
  async updateUser(payload: Partial<User> & { id?: string }): Promise<User> {
    if (payload.id) {
      const { data } = await apiClient.put<any>(`/v3/users/${payload.id}`, payload);
      return (data as any)?.result || data;
    }
    const { data } = await apiClient.post<any>('/user/update', payload);
    return (data as any)?.result || data;
  },

  /** Force-reset password (admin) */
  async forceSetPassword(payload: {
    userId: string;
    password: string;
  }): Promise<void> {
    await apiClient.post('/v3/reset-force-pwd', payload);
  },

  /** Look up user by email */
  async getUserByEmail(email: string): Promise<User> {
    const { data } = await apiClient.get<User>('/user/by_email', {
      params: { email },
    });
    return data;
  },

  /** Change current user's password */
  async changePassword(password: string): Promise<void> {
    await apiClient.post('/change-my-password', { password });
  },

  /** Request password recovery link */
  async recoverPassword(email: string): Promise<void> {
    await apiClient.post('/v3/recover-password', { email });
  },
};

export default UsersAPI;
